import { Injectable } from "@angular/core";

export interface CartItem {
  productId: string;
  productName: string;
  productImage: string;
  categoryName: string;
  price: number;
  discount: number;
  quantity: number;
  availableQuantity: number;
}

@Injectable({
  providedIn: "root"
})
export class CartService {
  private readonly storageKey = "kb_cart_v1";
  private readonly addressKey = "kb_cart_address_v1";
  private readonly igstRate = 3;

  getItems(): CartItem[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  saveItems(items: CartItem[]): void {
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }

  addItem(item: Omit<CartItem, "quantity"> & { quantity?: number }): CartItem[] {
    const items = this.getItems();
    const quantity = Number(item.quantity ?? 1);
    const index = items.findIndex((existing) => existing.productId === item.productId);

    if (index >= 0) {
      const nextQuantity = Math.min(
        Math.max(1, items[index].quantity + quantity),
        Math.max(1, Number(item.availableQuantity || items[index].availableQuantity || 1))
      );
      items[index] = {
        ...items[index],
        ...item,
        quantity: nextQuantity,
      };
    } else {
      items.push({
        ...item,
        quantity: Math.min(Math.max(1, quantity), Math.max(1, Number(item.availableQuantity || 1))),
      });
    }

    this.saveItems(items);
    return items;
  }

  updateQuantity(productId: string, quantity: number): CartItem[] {
    const items = this.getItems();
    const index = items.findIndex((item) => item.productId === productId);

    if (index >= 0) {
      const available = Math.max(1, Number(items[index].availableQuantity || 1));
      items[index].quantity = Math.min(Math.max(1, Number(quantity || 1)), available);
      this.saveItems(items);
    }

    return items;
  }

  removeItem(productId: string): CartItem[] {
    const items = this.getItems().filter((item) => item.productId !== productId);
    this.saveItems(items);
    return items;
  }

  clear(): void {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.addressKey);
  }

  setAddress(address: string): void {
    localStorage.setItem(this.addressKey, address);
  }

  getAddress(): string {
    return localStorage.getItem(this.addressKey) || "";
  }

  getItemCount(): number {
    return this.getItems().reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  getSubtotal(): number {
    return this.getItems().reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
  }

  getDiscountAmount(): number {
    return this.getItems().reduce((sum, item) => {
      const unitPrice = Number(item.price || 0);
      const discount = Number(item.discount || 0);
      const effectivePrice = discount > 0 ? unitPrice - (unitPrice * discount) / 100 : unitPrice;
      return sum + (unitPrice - effectivePrice) * Number(item.quantity || 0);
    }, 0);
  }

  getAmountAfterProductDiscount(): number {
    return this.getItems().reduce((sum, item) => {
      const unitPrice = Number(item.price || 0);
      const discount = Number(item.discount || 0);
      const effectivePrice = discount > 0 ? unitPrice - (unitPrice * discount) / 100 : unitPrice;
      return sum + effectivePrice * Number(item.quantity || 0);
    }, 0);
  }

  getOnlinePaymentDiscountAmount(onlinePaymentDiscountRate: number = 0): number {
    const rate = Math.min(100, Math.max(0, Number(onlinePaymentDiscountRate || 0)));
    return Number((this.getAmountAfterProductDiscount() * rate / 100).toFixed(2));
  }

  getTaxableAmount(onlinePaymentDiscountRate: number = 0): number {
    return Number((this.getAmountAfterProductDiscount() - this.getOnlinePaymentDiscountAmount(onlinePaymentDiscountRate)).toFixed(2));
  }

  getTaxAmount(onlinePaymentDiscountRate: number = 0): number {
    return Number((this.getTaxableAmount(onlinePaymentDiscountRate) * this.igstRate / 100).toFixed(2));
  }

  getTaxRate(): number {
    return this.igstRate;
  }

  getGrandTotal(onlinePaymentDiscountRate: number = 0): number {
    return Number((this.getTaxableAmount(onlinePaymentDiscountRate) + this.getTaxAmount(onlinePaymentDiscountRate)).toFixed(2));
  }

  getEffectiveUnitPrice(item: CartItem): number {
    const unitPrice = Number(item.price || 0);
    const discount = Number(item.discount || 0);
    return discount > 0 ? unitPrice - (unitPrice * discount) / 100 : unitPrice;
  }

  getLineTotal(item: CartItem): number {
    return this.getEffectiveUnitPrice(item) * Number(item.quantity || 0);
  }
}
